import { FC, ReactElement } from 'react';

import { RootState } from 'store/rootReducer';

import { useAppSelector } from 'hooks/defaultStoreHooks';

import { CenterActiveSourceGeometryButton } from 'components/CenterActiveSourceGeometryButton/CenterActiveSourceGeometryButton';
import { DrawerSection } from 'components/drawerSections/DrawerSections';
import GeometryFileDownload from 'components/geometryFileDownload/GeometryFileDownload';
import SkeletonImage from 'components/skeleton/SkeletonImage';

import { BebyggelseobjektUtkast, GeografiskIndelning } from 'models';

import { HandelseTypEnum } from 'enums';

import { getHandelseDateringDisplayNameForPresentation, getIconBySkydd } from 'utilities/bebyggelseobjektUtilities';

const SammansattBebyggelseTitleSection: FC<{ utkast: BebyggelseobjektUtkast }> = ({ utkast }): ReactElement => {
  const vinjett = useAppSelector((state: RootState) => state.vinjett.data);

  const getKommunAndLanText = (geografiskIndelning: GeografiskIndelning): string =>
    geografiskIndelning.kommun.map((kommun) => `${kommun.kommun_namn} kommun, ${kommun.lan_namn} län`).join(', ');
  const kategoriText = [utkast.ursprungligt_primar_andamal, ...(utkast.ursprungligt_sekundar_andamal ?? [])]
    .filter((a) => a !== null)
    .map((m) => m?.andamal_namn)
    .join(', ');

  return (
    <DrawerSection className="tw-border-hidden tw-p-4 md:tw-p-6">
      <h1 className="tw-heading-lg tw-mb-2 lg:tw-text-size-2.5">{utkast.titel}</h1>
      <div className="tw-flex tw-items-center tw-justify-between">
        {utkast.skydd && (
          <div className="tw-flex tw-items-center">
            <img
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                getIconBySkydd(utkast.skydd.skyddstyp, utkast.sammansatt?.havt_skydd || false)
              )}`}
              alt={`Ikon för skyddstyp ${utkast.skydd.skyddstyp_namn}${utkast.sammansatt?.havt_skydd && ' (HÄVT)'}`}
            />
            <p className="tw-heading-sm tw-ml-2 tw-flex tw-text-center">
              {utkast.skydd.skyddstyp_namn}
              {utkast.sammansatt?.havt_skydd && ' (HÄVT)'}
            </p>
          </div>
        )}
        {'geografisk_indelning' in utkast && (
          <p className="tw-flex">{getKommunAndLanText(utkast.geografisk_indelning)}</p>
        )}
      </div>

      <div className="tw-mt-4 tw-flex tw-flex-wrap">
        <table className="tw-shrink tw-grow tw-basis-[50%]">
          <tbody className="tw-align-top [&>tr>th]:tw-pr-2 [&>tr>th]:tw-text-left">
            <tr>
              <th>Bebyggelsenummer</th>
              <td>{utkast.bebyggelsenummer}</td>
            </tr>
            <tr>
              <th>Områdeskategori</th>
              <td>{kategoriText || '-'}</td>
            </tr>
            <tr>
              <th>Socken</th>
              <td>{utkast.geografisk_indelning.socken.map((s) => s.socken_namn).join(', ')}</td>
            </tr>
            <tr>
              <th>Förvaltare</th>
              <td>{utkast.skydd?.forvaltningsorganisationsnamn ?? '-'}</td>
            </tr>
            <tr>
              <th>Bebyggelsens tillkomst</th>
              <td>
                {getHandelseDateringDisplayNameForPresentation(
                  utkast.handelser?.find((h) => h.handelsetyp === HandelseTypEnum.TILLKOMST)
                )}
              </td>
            </tr>
            <tr>
              <th>Geometri</th>
              <td className="tw-flex tw-flex-wrap tw-gap-2">
                <CenterActiveSourceGeometryButton />
                {utkast.geometri && (
                  <GeometryFileDownload fileName={utkast.bebyggelsenummer || 'Okänd'} geometry={utkast.geometri} />
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="tw-flex tw-shrink tw-grow tw-basis-[175px] tw-flex-col tw-justify-start">
          {(vinjett?.thumbnailUrl && (
            <SkeletonImage
              className="tw-min-h-48 tw-w-full tw-object-cover"
              skeletonClassNames="tw-h-48 tw-w-full"
              src={vinjett.thumbnailUrl}
              alt="Vinjettbild"
            />
          )) || (
            <div className="tw-flex tw-h-48 tw-w-full tw-flex-col tw-items-center tw-justify-center tw-rounded-md tw-bg-raa-gray-3">
              <span className="raa-icon raa-icon-image tw-block"></span>
              Ingen bild
            </div>
          )}
        </div>
      </div>
    </DrawerSection>
  );
};

export default SammansattBebyggelseTitleSection;
